/**
 * Q-846 v4: the bookkeeping that keeps a role/grant read from outliving the session it was made for.
 *
 * AppShellProvider reads every grant from /api/access-control/context. v3 made a FAILED read fail closed.
 * This module closes the four ways a stale grant could still survive:
 *   1. a read that never answers (no timeout) kept the previous grants indefinitely → readContextWithTimeout,
 *      which bounds the headers AND the body under one timer;
 *   2. a 200 that lands after sign-out, a user switch or a NEWER read restored old grants → every write checks
 *      ticket.isCurrent(); every new read (full, silent or public) retires all older ones, and invalidate()
 *      retires every read in flight, so only the newest read can write;
 *   3. a different user's refresh was dropped while another refresh ran → start() never drops one;
 *   4. returning to a tab (or a bfcache restore) never re-read the grants → shouldRevalidateOnReturn().
 */

import { withDeadline } from '@/lib/http/fetchWithDeadline';

export const CONTEXT_FETCH_TIMEOUT_MS = 15_000;
export const RETURN_REVALIDATE_AFTER_MS = 60_000;

export interface RefreshTicket {
  readonly generation: number;
  /** False once sign-out, a user switch or a newer refresh has retired this read: drop its results. */
  isCurrent(): boolean;
}

export class AuthRefreshGate {
  private generation = 0;
  /** The full refresh in flight (user + its ticket's generation), for same-user de-duplication only. */
  private inFlight: { userId: string | null; generation: number } | null = null;

  /** Retire every read in flight: none of their results may be applied. */
  invalidate(): void {
    this.generation += 1;
    this.inFlight = null;
  }

  /**
   * Begin any read (public refetch, return-to-tab revalidation). It retires every older read, so a slower older
   * response can never land after a newer one and restore grants the newer read removed.
   */
  beginRead(): RefreshTicket {
    this.generation += 1;
    const generation = this.generation;
    return { generation, isCurrent: () => this.generation === generation };
  }

  /**
   * Begin a full refresh for `userId` (null = signed out). Returns null only when the SAME user's full refresh is
   * already running. A different user (or a sign-out) proceeds and retires the running one: it is never dropped.
   */
  start(userId: string | null): RefreshTicket | null {
    if (this.inFlight && userId !== null && this.inFlight.userId === userId) return null;
    const ticket = this.beginRead();
    this.inFlight = { userId, generation: ticket.generation };
    return ticket;
  }

  /** End a full refresh begun by start(), even if a newer read retired it (otherwise its user's refresh would be
   *  de-duplicated forever); a newer full refresh's bookkeeping is left alone. */
  finish(ticket: RefreshTicket): void {
    if (this.inFlight?.generation === ticket.generation) this.inFlight = null;
  }
}

/** Reject after `timeoutMs` if `promise` has not settled (the caller then fails closed). */
export function withReadTimeout<T>(promise: Promise<T>, timeoutMs: number, what: string): Promise<T> {
  return withDeadline(() => promise, timeoutMs, what);
}

/**
 * Read the context (headers AND JSON body) under ONE deadline (the shared lib/http helper): after `timeoutMs` the
 * request is aborted and the read rejects (and the caller fails closed) whether the headers or the body stalled.
 * A non-OK response is returned without parsing its body. A caller's `init.signal` still aborts the read.
 */
export function readContextWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs: number = CONTEXT_FETCH_TIMEOUT_MS,
  fetchImpl: typeof fetch = fetch,
): Promise<{ response: Response; data: unknown }> {
  return withDeadline(async (signal) => {
    const response = await fetchImpl(input, { ...init, signal });
    const data: unknown = response.ok ? await response.json() : undefined;
    return { response, data };
  }, timeoutMs, 'Auth context read', init.signal);
}

/**
 * Re-read the grants when the user comes back to the page: always after a bfcache restore (the page's JS state
 * is from before), and on a tab becoming visible once the last read is older than RETURN_REVALIDATE_AFTER_MS.
 */
export function shouldRevalidateOnReturn(
  trigger: { kind: 'pageshow'; persisted: boolean } | { kind: 'visibilitychange'; visible: boolean },
  lastReadAt: number,
  now: number,
): boolean {
  if (trigger.kind === 'pageshow') return trigger.persisted;
  return trigger.visible && now - lastReadAt >= RETURN_REVALIDATE_AFTER_MS;
}

/** Q-2415 emergency hotfix: Andrew/Prime explicitly requested three attempts with backoff.
 * Each attempt retains the existing headers+body timeout. Never invent grants on failure.
 * A retired session may finish its read, but must not start another attempt.
 */
export async function readContextWithRetry(
  input: string,
  init: RequestInit,
  timeoutMs: number = CONTEXT_FETCH_TIMEOUT_MS,
  fetchImpl: typeof fetch = fetch,
  isCurrent: () => boolean = () => true,
  attempts = 3,
  backoffMs = 500,
): Promise<{ response: Response; data: unknown }> {
  for (let attempt = 0; ; attempt += 1) {
    if (!isCurrent()) throw new Error('Auth context read retired');
    try {
      const result = await readContextWithTimeout(input, init, timeoutMs, fetchImpl);
      // A successful empty role set is authoritative; authentication refusals are not transient.
      if (result.response.ok || result.response.status < 500 || attempt + 1 >= attempts) return result;
    } catch (error) {
      if (attempt + 1 >= attempts || !isCurrent()) throw error;
    }
    await new Promise<void>(resolve => setTimeout(resolve, backoffMs * 2 ** attempt));
  }
}
